import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import {
  alpha,
  Box,
  CircularProgress,
  createTheme,
  CssBaseline,
  hexToRgb,
  LinearProgress,
  ThemeProvider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import TrustpageSummary from '../../components/TrustpageSummary/TrustpageSummary';
import TrustpageHeader from '../../components/TrustpageHeader/TrustpageHeader';
import TrustpageReviews from '../../components/TrustpageReviews/TrustpageReviews';
import TrustpageFooter from '../../components/TrustpageFooter/TrustpageFooter';
import TrustpageService from '../../services/TrustpageService';
import Section from '../../components/Main/Section/Section';
import { ITrustpageMonitoring } from '../../shared/interfaces/ITrustpageMonitoring';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ITrustpageSeal } from '../../shared/interfaces/ITrustpageSeal';

interface TrustpageEntry {
  aboutCompanyText: string;
  companyLogo: string;
  companyName: string;
  companyWebsiteUrl: string;
  contactEmail: string;
  copyrightText: string;
  createdAt: string;
  customFont: string;
  customFontFormat: string;
  id: string;
  imprintLink: string;
  lang: string;
  monitorings: ITrustpageMonitoring[];
  primaryColor: string;
  privacyPolicyLink: string;
  reviewCount: number;
  score: number;
  secondaryColor: string;
  slug: string;
  updatedAt: string;
  seal: ITrustpageSeal;
}

const Trustpage: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const { setCurrentView } = useContext(CurrentViewContext);
  const { id } = params;
  const isSmallScreen = useMediaQuery(theme.breakpoints.up('md'));

  const [loading, setLoading] = useState<boolean>(false);
  const [trustpage, setTrustpage] = useState<TrustpageEntry | null>(null);
  const [structuredData, setStructuredData] = useState<string | null>(null);
  const [metaScore, setMetaScore] = useState<number>(0);

  useEffect(() => {
    const controller = new AbortController();

    const getTrustPageInfo = async () => {
      const response = await TrustpageService.getTrustpagePublic(
        id!,
        setLoading,
        controller.signal
      );
      if (!response) return;
      setTrustpage(response);
      const roundedScore = Math.round(response.score * 10) / 10;
      setMetaScore(roundedScore);
    };

    (async () => await getTrustPageInfo())();

    return () => {
      controller.abort();
    };
  }, [id]);

  /*
    TODO: Set company name in title
   */
  useEffect(() => {
    setCurrentView(t('trustpage'));
    document.title = `Reputativ ${t('trustpage')}`;
  }, [setCurrentView, t]);

  /* useEffect(() => {
    if (!trustpage?.companyName) return;

    const data = {
      '@context': 'http://schema.org',
      '@type': 'Review',
      itemReviewed: {
        '@type': 'Organization',
        image: trustpage.companyLogo,
        name: trustpage.companyName,
        url: trustpage.companyWebsiteUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: trustpage.companyName,
        url: trustpage.companyWebsiteUrl,
      },
      author: {
        '@type': 'Person',
        name: trustpage.companyName,
        url: trustpage.companyWebsiteUrl,
      },
      title: `Erfahrungen mit ${trustpage.companyName} | ${trustpage.reviewCount} Bewertungen`,
      reviewBody: `Diese Erfahrungen haben andere mit ${trustpage.companyName} geteilt. Jetzt Bewerten! `,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '4',
      },
      datePublished: trustpage.createdAt,
    };
    setStructuredData(JSON.stringify(data));
  }, [trustpage]);*/

  const getFontFormatByFileExtension = (fontFormat: FontFileExtensions) => {
    return {
      woff2: 'woff2',
      woff: 'woff',
      ttf: 'truetype',
    }[fontFormat];
  };

  if (trustpage) {
    const customTheme = createTheme({
      palette: {
        primary: {
          main: theme.palette.primary.light,
        },
        divider: trustpage.secondaryColor
          ? alpha(hexToRgb(trustpage.secondaryColor), 0.2)
          : theme.palette.divider,
      },
      typography: {
        fontFamily:
          trustpage.customFont && trustpage.customFontFormat
            ? `"${trustpage.id}"`
            : '"Arial", "Helvetica", sans-serif',
      },
      components: {
        MuiCssBaseline: {
          styleOverrides:
            trustpage.customFont && trustpage.customFontFormat
              ? `
           @font-face {
            font-family: "${trustpage.id}";
            src: url(${trustpage.customFont}) format("${getFontFormatByFileExtension(
                  trustpage.customFontFormat as FontFileExtensions
                )}");
           }
        `
              : undefined,
        },
      },
    });

    return (
      <ThemeProvider theme={customTheme}>
        <Helmet prioritizeSeoTags={true}>
          <link
            rel={'icon'}
            type={'image/png'}
            sizes={'16x16'}
            href={trustpage.companyLogo}
          />
          <title>{`${trustpage.companyName}`}</title>
          <meta
            property={'og:description'}
            content={trustpage.companyWebsiteUrl}
          />
          <meta
            name={'description'}
            content={`Diese Erfahrungen haben andere mit ${trustpage.companyName} geteilt. Jetzt Bewerten!`}
          />
          <meta
            name="robots"
            content="index,follow"
          />
          <meta
            name={'rating'}
            content={` Bewertung ${metaScore} von 5 Sternen | ${trustpage.reviewCount} Ergebnisse`}
          />
        </Helmet>
        <CssBaseline />
        {loading && <LinearProgress />}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
            backgroundColor: '#f1f1f1',
          }}
        >
          <TrustpageHeader
            companyName={trustpage.companyName}
            companyLogo={trustpage.companyLogo}
            score={trustpage.score}
            reviewCount={trustpage.reviewCount}
            companyWebsiteUrl={trustpage.companyWebsiteUrl}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: theme.spacing(3),
              px: theme.spacing(2),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: isSmallScreen ? 'row' : 'column',
                minWidth: 'auto',
                maxWidth: '1366px',
                alignItems: isSmallScreen ? 'flex-start' : 'center',
                flex: 1,
              }}
            >
              <TrustpageSummary
                companyName={trustpage.companyName}
                aboutCompanyText={trustpage.aboutCompanyText}
                monitorings={trustpage.monitorings}
                score={trustpage.score}
                seal={trustpage.seal}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  ml: isSmallScreen ? theme.spacing(4) : 0,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    m: isSmallScreen ? theme.spacing(0) : theme.spacing(4),
                    textAlign: isSmallScreen ? 'none' : 'center',
                  }}
                >
                  {t('experience_and_reviews')}
                </Typography>
                <TrustpageReviews
                  trustpageId={trustpage.id}
                  monitorings={trustpage.monitorings}
                  primaryColor={trustpage.primaryColor}
                  setLoading={setLoading}
                  commentingModule={true}
                />
              </Box>
            </Box>
          </Box>
          <TrustpageFooter
            companyName={trustpage.companyName}
            copyrightText={trustpage.copyrightText}
            imprintLink={trustpage.imprintLink}
            contactEmail={trustpage.contactEmail}
            privacyPolicyLink={trustpage.privacyPolicyLink}
          />
        </Box>
      </ThemeProvider>
    );
  } else {
    return (
      <Section>
        <Box
          sx={{
            display: 'flex',
            alignSelf: 'center',
            alignItems: 'center',
            flex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      </Section>
    );
  }
};

export default Trustpage;
